import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "../../api/axios";
import jwt_decode from "jwt-decode";
import Select from "react-select";
import Swal from "sweetalert2";

import { LogoHandler } from "./Helpers/LogoHandler";
import { DocumentsHandler } from "./Helpers/DocumentsHandler";

import {
  Form,
  Label,
  Input,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Row,
  Col,
  Alert,
} from "reactstrap";

const GET_LAYOUT_URL = "/layout/all";
const GET_PROPERTY_URL = "/property/get-property";
const EDIT_PROPERTY_URL = "/property/edit-property/";
const UPLOAD_FILES_URL = "/property/upload-files";

const ErrorAlert = ({ formik }) => {
  const getAllErrors = () => {
    const errors = [];
    const flattenErrors = (errorObj, touchedObj, prefix = '') => {
      Object.keys(errorObj).forEach((key) => {
        const path = prefix ? `${prefix}.${key}` : key;
        const errorVal = errorObj[key];
        const touchedVal = touchedObj?.[key];
        if (typeof errorVal === 'string') {
          if (touchedVal) {
            errors.push({
              field: path.replace(/\./g, ' → ').replace(/\[\d+\]/g, (m) => ` ${m}`),
              message: capitalizeFirstLetter(errorVal),
              path: path
            });
          }
        } else if (typeof errorVal === 'object' && errorVal !== null) {
          flattenErrors(errorVal, touchedVal || {}, path);
        }
      });
    };
    flattenErrors(formik.errors, formik.touched);
    return errors;
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return string;
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const errors = getAllErrors();

  if (errors.length === 0) {
    return null;
  }

  return (
    <Alert className="mb-4 border-0 shadow-lg" style={{
      background: 'linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)',
      borderRadius: '12px'
    }}>
      <Card className="border-0" style={{ backgroundColor: 'transparent' }}>
        <CardHeader className="bg-transparent border-0 pb-2 px-0">
          <Row className="align-items-center">
            <Col>
              <h6 className="mb-0 d-flex align-items-center" style={{ color: '#c53030' }}>
                <span className="me-2" style={{ fontSize: '1.2rem' }}>⚠️</span>
                <span className="fw-bold">Form Validation Errors</span>
              </h6>
            </Col>
            <Col xs="auto">
              <Badge
                pill
                className="px-3 py-2"
                style={{
                  backgroundColor: '#c53030',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}
              >
                {errors.length} error{errors.length !== 1 ? 's' : ''}
              </Badge>
            </Col>
          </Row>
        </CardHeader>
        <CardBody className="px-0 pt-2">
          {errors.map((error, index) => (
            <Alert
              key={index}
              className="mb-2 py-3 px-4 border-0 shadow-sm"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #fef5e7 100%)',
                borderLeft: '5px solid #e53e3e',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateX(2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(229, 62, 62, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
              }}
            >
              <Row className="align-items-center">
                <Col xs="12" md="4">
                  <div className="d-flex align-items-center mb-2 mb-md-0">
                    <span
                      className="me-2"
                      style={{
                        color: '#744210',
                        fontSize: '0.875rem'
                      }}
                    >
                      📍
                    </span>
                    <small
                      className="fw-bold"
                      style={{
                        color: '#744210',
                        fontSize: '0.8rem',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {error.field}
                    </small>
                  </div>
                </Col>
                <Col xs="12" md="8">
                  <div className="d-flex align-items-center">
                    <span
                      className="me-2"
                      style={{
                        color: '#c53030',
                        fontSize: '0.875rem'
                      }}
                    >
                      ❌
                    </span>
                    <span
                      style={{
                        color: '#c53030',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      {error.message}
                    </span>
                  </div>
                </Col>
              </Row>
            </Alert>
          ))}
        </CardBody>
      </Card>
    </Alert>
  );
};

const EditProperty = ({ propertyId, onSuccess }) => {
  const [layouts, setLayouts] = useState([]);
  const token = localStorage.getItem("accessToken") || "";
  const decode = token ? jwt_decode(token) : null;

  useEffect(() => {
    const getLayout = async () => {
      if (decode?.role === "company") {
        try {
          const response = await axios.post(GET_LAYOUT_URL, {
            companyID: decode.id,
          });
          const layoutOptions = response.data.layouts.map((layout) => ({
            label: layout.layoutName,
            value: layout._id,
          }));
          setLayouts(layoutOptions);
        } catch (error) {
          console.error("Error fetching layouts:", error);
        }
      }
    };

    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${GET_PROPERTY_URL}/${propertyId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 200 && response.data.data) {
          const propertyData = response.data.data;
          let documentData = [];

          if (propertyData.documents && Array.isArray(propertyData.documents)) {
            documentData = propertyData.documents.map((doc) => ({
              originalUrl: doc.url,
              name: doc.fileName || "Document",
              fileType: doc.url.split(".").pop().toLowerCase(),
              uploadDate: doc.uploadDate,
            }));
          }

          let logoData = null;
          if (typeof propertyData.logo === "object" && propertyData.logo?.url) {
            logoData = {
              originalUrl: propertyData.logo.url,
              name: propertyData.logo.fileName || "Logo",
              fileType: propertyData.logo.url.split(".").pop().toLowerCase(),
              uploadDate: propertyData.logo.uploadDate,
            };
          } else if (propertyData.logo) {
            logoData = {
              originalUrl: propertyData.logo,
              name: "Logo",
              fileType: propertyData.logo.split(".").pop().toLowerCase(),
            };
          }

          formik.setValues({
            ...formik.values,
            _id: propertyData._id,
            primary_id: propertyData.primaryID,
            title: propertyData.title || "",
            landlord: propertyData.landlord || "",
            layouts: propertyData.layouts || [],
            partnership_label: propertyData.partnership_label || "",
            logo: logoData,
            province: propertyData.province || "",
            city: propertyData.city || "",
            postal_code: propertyData.postal_code || "",
            address: propertyData.address || "",
            status: propertyData.status,
            incentives: propertyData.incentives || [],
            appliances: propertyData.appliances || [],
            currency: propertyData.currency || "",
            building: propertyData?.building?.length
              ? propertyData.building.map((building) => ({
                ...building,
                name: building.name || building.buildingName || "",
                postalCode: building.postalCode || '',
                type: building.type || "Apartment",
                utilities_available: building.utilities_available || {
                  heat: false,
                  water_waste: false,
                  electricity: false,
                },
                utilities_paid_by_tenant: building.utilities_paid_by_tenant || {
                  heat: false,
                  water_waste: false,
                  electricity: false,
                },
                pet_fees: building.pet_fees || {
                  cat_fee: 0,
                  dog_fee: 0,
                  other_fee: 0,
                },
                allowed_pets: building.allowed_pets || {
                  dogs: true,
                  cats: true,
                  others: true,
                },
                custom_utilities_clause: building.custom_utilities_clause || {
                  enabled: false,
                  text: "",
                },
              }))
              : [],
            documents: documentData,
            documents_to_request: propertyData.documents_to_request || [],
          });
        }
      } catch (error) {
        console.error("Error fetching property:", error);
      }
    };

    getLayout();
    if (propertyId) {
      fetchProperty();
    }
  }, [decode?.role, decode?.id, propertyId]);

  const formik = useFormik({
    initialValues: {
      _id: "",
      primary_id: "",
      company_id: decode?.id || "",
      title: "",
      landlord: "",
      layouts: [],
      partnership_label: "",
      city: "",
      province: "",
      postal_code: "",
      status: true,
      address: "",
      currency: "",
      incentives: ["Telus (Basic Cable & Internet 500)"],
      appliances: [
        "Air Conditioning",
        "Blinds",
        "Dishwasher",
        "Dryer",
        "Over the range Microwave",
        "Range/Stove",
        "Refrigerator",
        "Washer",
      ],
      building: [
        {
          name: "",
          postalCode: "",
          type: "Apartment",
          utilities_available: {
            heat: false,
            water_waste: false,
            electricity: false,
          },
          utilities_paid_by_tenant: {
            heat: false,
            water_waste: false,
            electricity: false,
          },
          pet_fees: {
            cat_fee: 0,
            dog_fee: 0,
            other_fee: 0,
          },
          custom_utilities_clause: {
            enabled: false,
            text: "",
          },
          allowed_pets: {
            dogs: true,
            cats: true,
            others: true,
          },
        },
      ],
      logo: "",
      documents: [],
      documents_to_request: [],
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Property Name is required!"),
      landlord: Yup.string().required("Landlord is required!"),
      layouts: Yup.array().min(1, "Please select a Layout!"),
      city: Yup.string()
        .matches(/^[a-zA-Z0-9 ]+$/, "Only letters and numbers are allowed!")
        .required("City is required!"),
      province: Yup.string().required("Province is required!"),
      postal_code: Yup.string()
        .matches(/^[a-zA-Z0-9 ]+$/, "Only letters and numbers are allowed!")
        .required("Postal code is required!"),
      address: Yup.string().required("Address is required!"),
      currency: Yup.string().required("Currency is required!"),
      building: Yup.array().of(
        Yup.object({
          name: Yup.string().trim(),
          postalCode: Yup.string().trim(),
          type: Yup.string()
            .oneOf(["Apartment", "Townhouse"], "Please select Apartment or Townhouse")
            .required("Building type is required"),
          pet_fees: Yup.object({
            cat_fee: Yup.number().min(0).required("Cat fee is required"),
            dog_fee: Yup.number().min(0).required("Dog fee is required"),
            other_fee: Yup.number().min(0).required("Other fee is required"),
          }),
        })
      ),
      documents_to_request: Yup.array().of(
        Yup.string().required("Document name is required!")
      ),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const propertyData = { ...values };

        if (Array.isArray(propertyData.documents)) {
          propertyData.documents = propertyData.documents.map((doc) => {
            if (doc.file instanceof File) {
              return {
                fileName: doc.name || doc.file.name,
                url: doc.originalUrl || "pending-upload",
              };
            }
            return {
              fileName: doc.name,
              url: doc.originalUrl,
              uploadDate: doc.uploadDate || new Date(),
            };
          });
        }

        if (propertyData.logo) {
          if (propertyData.logo.file instanceof File) {
            propertyData.logo = {
              fileName: propertyData.logo.name || propertyData.logo.file.name,
              url: propertyData.logo.originalUrl || "pending-upload",
            };
          } else if (propertyData.logo.originalUrl) {
            propertyData.logo = {
              fileName: propertyData.logo.name,
              url: propertyData.logo.originalUrl,
              uploadDate: propertyData.logo.uploadDate || new Date(),
            };
          }
        }

        let propertyId = values._id;
        if (propertyId) {
          const updateResponse = await axios.post(EDIT_PROPERTY_URL, propertyData, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (updateResponse.status !== 200) {
            throw new Error(updateResponse.data.message || "Failed to update property.");
          }
        } else {
          const createResponse = await axios.post(EDIT_PROPERTY_URL, propertyData, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (createResponse.status !== 201) {
            throw new Error(createResponse.data.message || "Failed to create property.");
          }

          propertyId = createResponse.data.data._id;
        }

        const hasNewLogoFile = values.logo?.file instanceof File;
        const hasNewDocumentFiles = Array.isArray(values.documents) &&
          values.documents.some((doc) => doc.file instanceof File);

        if (hasNewLogoFile || hasNewDocumentFiles) {
          const fileFormData = new FormData();

          if (hasNewLogoFile) {
            fileFormData.append("logo", values.logo.file);
            fileFormData.append("logoFileName", values.logo.name || values.logo.file.name);
          }

          if (hasNewDocumentFiles) {
            const documentFileNames = [];
            values.documents.forEach((fileObj) => {
              if (fileObj.file instanceof File) {
                fileFormData.append("documents", fileObj.file);
                documentFileNames.push(fileObj.name || fileObj.file.name);
              }
            });

            if (documentFileNames.length > 0) {
              fileFormData.append("documentFileNames", JSON.stringify(documentFileNames));
            }
          }

          const uploadResponse = await axios.post(`${UPLOAD_FILES_URL}/${propertyId}`, fileFormData, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          });

          if (uploadResponse.status !== 200) {
            throw new Error(uploadResponse.data.message || "File upload failed.");
          }
        }

        Swal.fire({
          icon: "success",
          title: "Property Updated!",
          text: "The property has been updated successfully.",
        }).then(() => {
          if (onSuccess) {
            onSuccess();
          }
        });
      } catch (error) {
        console.error("Error updating property:", error);
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.response?.data?.message || "Something went wrong. Try again!",
        });
      }
    },
  });

  const addIncentive = () => {
    formik.setFieldValue("incentives", [...formik.values.incentives, ""]);
  };

  const removeIncentive = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this incentive?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedIncentives = [...formik.values.incentives];
        updatedIncentives.splice(index, 1);
        formik.setFieldValue("incentives", updatedIncentives);
        Swal.fire("Removed!", "The incentive has been removed.", "success");
      }
    });
  };

  const addAppliance = () => {
    formik.setFieldValue("appliances", [...formik.values.appliances, ""]);
  };

  const removeAppliance = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this appliance?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedAppliances = [...formik.values.appliances];
        updatedAppliances.splice(index, 1);
        formik.setFieldValue("appliances", updatedAppliances);
        Swal.fire("Removed!", "The appliance has been removed.", "success");
      }
    });
  };

  const addDocumentRequest = () => {
    formik.setFieldValue("documents_to_request", [
      ...formik.values.documents_to_request,
      "",
    ]);
  };

  const removeDocumentRequest = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this document request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedDocuments = [...formik.values.documents_to_request];
        updatedDocuments.splice(index, 1);
        formik.setFieldValue("documents_to_request", updatedDocuments);
        Swal.fire("Removed!", "The document request has been removed.", "success");
      }
    });
  };

  const handleDocumentRequestChange = (index, value) => {
    const updatedDocuments = [...formik.values.documents_to_request];
    updatedDocuments[index] = value;
    formik.setFieldValue("documents_to_request", updatedDocuments);
  };

  const handleBuildingChange = (index, field, value) => {
    const updatedBuildings = [...formik.values.building];
    updatedBuildings[index][field] = value;
    formik.setFieldValue("building", updatedBuildings);
  };

  const handleUtilityChange = (index, utility, value) => {
    const updatedBuildings = [...formik.values.building];
    updatedBuildings[index].utilities_available[utility] = value;
    formik.setFieldValue("building", updatedBuildings);
  };

  const handleUtilityPaidByTenantChange = (index, utility, value) => {
    const updatedBuildings = [...formik.values.building];
    if (!updatedBuildings[index].utilities_paid_by_tenant) {
      updatedBuildings[index].utilities_paid_by_tenant = {
        heat: false,
        water_waste: false,
        electricity: false,
      };
    }
    updatedBuildings[index].utilities_paid_by_tenant[utility] = value;
    formik.setFieldValue("building", updatedBuildings);
  };

  const handleCustomUtilitiesClauseChange = (index, field, value) => {
    const updatedBuildings = [...formik.values.building];
    if (!updatedBuildings[index].custom_utilities_clause) {
      updatedBuildings[index].custom_utilities_clause = { enabled: false, text: "" };
    }
    updatedBuildings[index].custom_utilities_clause[field] = value;
    formik.setFieldValue("building", updatedBuildings);
  };

  const handlePetFeeChange = (index, feeType, value) => {
    const updatedBuildings = [...formik.values.building];
    if (!updatedBuildings[index].pet_fees) {
      updatedBuildings[index].pet_fees = {
        cat_fee: 0,
        dog_fee: 0,
        other_fee: 0,
      };
    }
    updatedBuildings[index].pet_fees[feeType] = value;
    formik.setFieldValue("building", updatedBuildings);
  };

  const handleBuildingTypeChange = (index, value) => {
    const updatedBuildings = [...formik.values.building];
    updatedBuildings[index].type = value;
    formik.setFieldValue("building", updatedBuildings);
  };

  const handleAllowedPetChange = (index, petType, value) => {
    const updatedBuildings = [...formik.values.building];
    if (!updatedBuildings[index].allowed_pets) {
      updatedBuildings[index].allowed_pets = {
        dogs: false,
        cats: false,
        others: false,
      };
    }
    updatedBuildings[index].allowed_pets[petType] = value;
    formik.setFieldValue("building", updatedBuildings);
  };

  const addBuilding = () => {
    formik.setFieldValue("building", [
      ...formik.values.building,
      {
        name: "",
        postalCode: "",
        type: "Apartment",
        utilities_available: {
          heat: false,
          water_waste: false,
          electricity: false,
        },
        utilities_paid_by_tenant: {
          heat: false,
          water_waste: false,
          electricity: false,
        },
        pet_fees: {
          cat_fee: 0,
          dog_fee: 0,
          other_fee: 0,
        },
        allowed_pets: {
          dogs: true,
          cats: true,
          others: true,
        },
        custom_utilities_clause: {
          enabled: false,
          text: "",
        },
      },
    ]);
  };

  const removeBuilding = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this building?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedBuildings = formik.values.building.filter((_, i) => i !== index);
        formik.setFieldValue("building", updatedBuildings);
        Swal.fire("Removed!", "The building has been removed.", "success");
      }
    });
  };

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  return (
    <Form
      onSubmit={(e) => {
        if (
          !e.nativeEvent.submitter ||
          !e.nativeEvent.submitter.classList.contains("save-user")
        ) {
          e.preventDefault();
        } else {
          formik.handleSubmit(e);
        }
      }}
    >
      <div className="row">
        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label" htmlFor="formrow-title-input">
              Property Name *
            </Label>
            <Input
              type="text"
              name="title"
              id="formrow-title-input"
              className="form-control"
              placeholder="Enter a Property Name"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.title && formik.errors.title ? true : false}
            />
            {formik.touched.title && formik.errors.title && (
              <Label style={{ color: "red" }}>{formik.errors.title}</Label>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label" htmlFor="formrow-landlord-input">
              Landlord *
            </Label>
            <Input
              type="text"
              name="landlord"
              id="formrow-landlord-input"
              className="form-control"
              placeholder="Enter a Landlord"
              value={formik.values.landlord}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.landlord && formik.errors.landlord ? true : false}
            />
            {formik.touched.landlord && formik.errors.landlord && (
              <Label style={{ color: "red" }}>{formik.errors.landlord}</Label>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label" htmlFor="formrow-layouts-input">
              Layouts *
            </Label>
            <Select
              isMulti
              name="layouts"
              id="formrow-layouts-input"
              options={layouts}
              value={formik.values.layouts.map((value) =>
                layouts.find((option) => option.value === value)
              )}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions.map((option) => option.value);
                formik.setFieldValue("layouts", selectedValues);
              }}
              onBlur={() => formik.setFieldTouched("layouts", true)}
              className="basic-multi-select"
              classNamePrefix="select"
            />
            {formik.touched.layouts && formik.errors.layouts && (
              <Label style={{ color: "red" }}>{formik.errors.layouts}</Label>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label" htmlFor="formrow-partnership-input">
              Partnership Label
            </Label>
            <Input
              type="text"
              name="partnership_label"
              id="formrow-partnership-input"
              className="form-control"
              placeholder="Enter Partnership Label"
              value={formik.values.partnership_label}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label" htmlFor="formrow-city-input">
              City *
            </Label>
            <Input
              type="text"
              name="city"
              id="formrow-city-input"
              className="form-control"
              placeholder="Enter a city name"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.city && formik.errors.city ? true : false}
            />
            {formik.touched.city && formik.errors.city && (
              <Label style={{ color: "red" }}>{formik.errors.city}</Label>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label" htmlFor="formrow-province-input">
              Province *
            </Label>
            <Input
              type="text"
              name="province"
              id="formrow-province-input"
              className="form-control"
              placeholder="Enter province"
              value={formik.values.province}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.province && formik.errors.province ? true : false}
            />
            {formik.touched.province && formik.errors.province && (
              <Label style={{ color: "red" }}>{formik.errors.province}</Label>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label" htmlFor="formrow-postal-input">
              Postal Code *
            </Label>
            <Input
              type="text"
              name="postal_code"
              id="formrow-postal-input"
              className="form-control"
              placeholder="Enter postal code"
              value={formik.values.postal_code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.postal_code && formik.errors.postal_code ? true : false}
            />
            {formik.touched.postal_code && formik.errors.postal_code && (
              <Label style={{ color: "red" }}>{formik.errors.postal_code}</Label>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label" htmlFor="formrow-status-input">
              Status *
            </Label>
            <Select
              name="status"
              id="formrow-status-input"
              value={statusOptions.find((option) => option.value === formik.values.status) || null}
              onChange={(selectedOption) => {
                formik.setFieldValue("status", selectedOption ? selectedOption.value : "");
              }}
              options={statusOptions}
              isClearable
            />
            {formik.touched.status && formik.errors.status && (
              <Label style={{ color: "red" }}>{formik.errors.status}</Label>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label" htmlFor="formrow-currency-input">
              Currency *
            </Label>
            <Input
              type="text"
              name="currency"
              id="formrow-currency-input"
              className="form-control"
              placeholder="Enter currency"
              value={formik.values.currency}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.currency && formik.errors.currency ? true : false}
            />
            {formik.touched.currency && formik.errors.currency && (
              <Label style={{ color: "red" }}>{formik.errors.currency}</Label>
            )}
          </div>
        </div>

        <div className="col-md-12">
          <div className="mb-3">
            <Label className="form-label" htmlFor="formrow-address-input">
              Address *
            </Label>
            <Input
              type="textarea"
              name="address"
              id="formrow-address-input"
              className="form-control"
              placeholder="Enter your address"
              rows="3"
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.address && formik.errors.address ? true : false}
            />
            {formik.touched.address && formik.errors.address && (
              <Label style={{ color: "red" }}>{formik.errors.address}</Label>
            )}
          </div>
        </div>

        <div className="col-md-12">
          <div className="mb-3">
            {formik.values.building.map((building, index) => (
              <div key={index} className="row align-items-center mt-3 p-2 border rounded mb-3">
                <div className="col-md-12 mb-3">
                  <Label className="fw-bold">Building Address</Label>
                  <Input
                    type="text"
                    value={building.name}
                    onChange={(e) => handleBuildingChange(index, "name", e.target.value)}
                    placeholder="Enter building name"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <Label className="fw-bold">Building Type *</Label>
                  <Select
                    name={`building[${index}].type`}
                    options={[
                      { value: "Apartment", label: "Apartment" },
                      { value: "Townhouse", label: "Townhouse" },
                    ]}
                    value={
                      building.type
                        ? { value: building.type, label: building.type }
                        : null
                    }
                    onChange={(option) => handleBuildingTypeChange(index, option?.value || "")}
                    onBlur={() => formik.setFieldTouched(`building[${index}].type`, true)}
                    placeholder="Select building type"
                    isClearable={false}
                    className="basic-single"
                    classNamePrefix="select"
                  />
                  {formik.touched.building?.[index]?.type && formik.errors.building?.[index]?.type && (
                    <div className="text-danger small mt-1">
                      {formik.errors.building[index].type}
                    </div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <Label className="fw-bold">Postal Code</Label>
                  <Input
                    type="text"
                    value={building.postalCode || ""}
                    onChange={(e) => handleBuildingChange(index, "postalCode", e.target.value)}
                    placeholder="Enter postal code"
                  />
                </div>
                <div className="col-md-12 mb-3">
                  <Label className="fw-bold">Utilities Included:</Label>
                  <div className="d-flex align-items-center">
                    <div className="form-check form-check-inline">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={building.utilities_available.heat}
                        onChange={(e) => handleUtilityChange(index, "heat", e.target.checked)}
                      />
                      <Label className="form-check-label">Heat</Label>
                    </div>
                    <div className="form-check form-check-inline">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={building.utilities_available.water_waste}
                        onChange={(e) => handleUtilityChange(index, "water_waste", e.target.checked)}
                      />
                      <Label className="form-check-label">Water/Waste</Label>
                    </div>
                    <div className="form-check form-check-inline">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={building.utilities_available.electricity}
                        onChange={(e) => handleUtilityChange(index, "electricity", e.target.checked)}
                      />
                      <Label className="form-check-label">Electricity</Label>
                    </div>
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <Label className="fw-bold">Utilities Paid by Tenant:</Label>
                  <div className="d-flex align-items-center">
                    <div className="form-check form-check-inline">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={building.utilities_paid_by_tenant?.heat || false}
                        onChange={(e) => handleUtilityPaidByTenantChange(index, "heat", e.target.checked)}
                      />
                      <Label className="form-check-label">Heat</Label>
                    </div>
                    <div className="form-check form-check-inline">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={building.utilities_paid_by_tenant?.water_waste || false}
                        onChange={(e) => handleUtilityPaidByTenantChange(index, "water_waste", e.target.checked)}
                      />
                      <Label className="form-check-label">Water/Waste</Label>
                    </div>
                    <div className="form-check form-check-inline">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={building.utilities_paid_by_tenant?.electricity || false}
                        onChange={(e) => handleUtilityPaidByTenantChange(index, "electricity", e.target.checked)}
                      />
                      <Label className="form-check-label">Electricity</Label>
                    </div>
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="form-check">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      checked={building.custom_utilities_clause?.enabled || false}
                      onChange={(e) => handleCustomUtilitiesClauseChange(index, "enabled", e.target.checked)}
                    />
                    <Label className="form-check-label fw-bold">
                      Add custom utilities clause to lease
                    </Label>
                  </div>
                  {building.custom_utilities_clause?.enabled && (
                    <div className="mt-2">
                      <Input
                        type="textarea"
                        rows={5}
                        value={building.custom_utilities_clause?.text || ""}
                        onChange={(e) => handleCustomUtilitiesClauseChange(index, "text", e.target.value)}
                        placeholder="Wording appended to clause 7 (UTILITIES AND SERVICE) of every new lease for this building."
                      />
                      <small className="text-muted">
                        Appears at the end of clause 7. Applies to leases created from now on; existing leases keep the wording captured when they were created.
                      </small>
                    </div>
                  )}
                </div>

                <div className="col-md-12 mb-3">
                  <Label className="fw-bold">Allowed Pets:</Label>
                  <div className="d-flex align-items-center">
                    <div className="form-check form-check-inline">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={building.allowed_pets?.dogs || false}
                        onChange={(e) => handleAllowedPetChange(index, "dogs", e.target.checked)}
                      />
                      <Label className="form-check-label">Dogs</Label>
                    </div>
                    <div className="form-check form-check-inline">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={building.allowed_pets?.cats || false}
                        onChange={(e) => handleAllowedPetChange(index, "cats", e.target.checked)}
                      />
                      <Label className="form-check-label">Cats</Label>
                    </div>
                    <div className="form-check form-check-inline">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={building.allowed_pets?.others || false}
                        onChange={(e) => handleAllowedPetChange(index, "others", e.target.checked)}
                      />
                      <Label className="form-check-label">Other Pets</Label>
                    </div>
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <Label className="fw-bold">Pet Fees:</Label>
                  <div className="row">
                    <div className="col-md-4">
                      <Label>Cat Fee</Label>
                      <Input
                        type="number"
                        min="0"
                        value={building.pet_fees?.cat_fee || 0}
                        onChange={(e) =>
                          handlePetFeeChange(index, "cat_fee", parseFloat(e.target.value) || 0)
                        }
                        placeholder="Enter cat fee"
                      />
                    </div>
                    <div className="col-md-4">
                      <Label>Dog Fee</Label>
                      <Input
                        type="number"
                        min="0"
                        value={building.pet_fees?.dog_fee || 0}
                        onChange={(e) =>
                          handlePetFeeChange(index, "dog_fee", parseFloat(e.target.value) || 0)
                        }
                        placeholder="Enter dog fee"
                      />
                    </div>
                    <div className="col-md-4">
                      <Label>Other Pet Fee</Label>
                      <Input
                        type="number"
                        min="0"
                        value={building.pet_fees?.other_fee || 0}
                        onChange={(e) =>
                          handlePetFeeChange(index, "other_fee", parseFloat(e.target.value) || 0)
                        }
                        placeholder="Enter other pet fee"
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-12 text-end">
                  <Button color="danger" onClick={() => removeBuilding(index)}>
                    Remove Building
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              color="light"
              onClick={addBuilding}
              style={{ marginTop: "10px" }}
            >
              <i className="bx bx-plus me-1"></i> Add Building
            </Button>
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label fw-bold">Incentives</Label>
            {formik.values.incentives.map((incentive, index) => (
              <div
                key={index}
                style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}
              >
                <Input
                  type="text"
                  name={`incentives[${index}]`}
                  className="form-control"
                  value={incentive}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter incentive"
                />
                <Button
                  type="button"
                  color="danger"
                  onClick={() => removeIncentive(index)}
                  style={{ marginLeft: "5px" }}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              color="light"
              onClick={addIncentive}
              style={{ marginTop: "10px" }}
            >
              <i className="bx bx-plus me-1"></i> Add Incentive Option
            </Button>
          </div>
        </div>

        <div className="col-md-6">
          <div className="mb-3">
            <Label className="form-label fw-bold">Appliances</Label>
            {formik.values.appliances.map((appliance, index) => (
              <div
                key={index}
                style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}
              >
                <Input
                  type="text"
                  name={`appliances[${index}]`}
                  className="form-control"
                  value={appliance}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter appliance"
                />
                <Button
                  type="button"
                  color="danger"
                  onClick={() => removeAppliance(index)}
                  style={{ marginLeft: "5px" }}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              color="light"
              onClick={addAppliance}
              style={{ marginTop: "10px" }}
            >
              <i className="bx bx-plus me-1"></i> Add Appliance
            </Button>
          </div>
        </div>

        <div className="row mt-4 ">
          <div className="col-md-6">
            <DocumentsHandler
              key={`documents`}
              name={`documents`}
              label="Upload Property Documents *"
              formik={formik}
            />
          </div>
          <div className="col-md-6">
            <LogoHandler
              key={`logo`}
              name={`logo`}
              label="Upload Property Logo *"
              formik={formik}
            />
          </div>
        </div>

        <div className="col-md-12 mb-4">
          <div className="mb-3">
            <Label className="form-label fw-bold">Documents to Request</Label>
            {formik.values.documents_to_request.map((doc, index) => (
              <div key={index} className="row align-items-center mt-3 p-2 border rounded mb-3">
                <div className="col-md-10 mb-3">
                  <Label>Document Name</Label>
                  <Input
                    type="text"
                    name={`documents_to_request[${index}]`}
                    value={doc}
                    onChange={(e) => handleDocumentRequestChange(index, e.target.value)}
                    placeholder="Enter document name"
                    className="form-control"
                    invalid={
                      formik.touched.documents_to_request?.[index] &&
                      formik.errors.documents_to_request?.[index]
                    }
                  />
                  {formik.touched.documents_to_request?.[index] &&
                    formik.errors.documents_to_request?.[index] && (
                      <Label style={{ color: "red" }}>
                        {formik.errors.documents_to_request[index]}
                      </Label>
                    )}
                </div>
                <div className="col-md-2 text-end">
                  <Button color="danger" onClick={() => removeDocumentRequest(index)}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              color="light"
              onClick={addDocumentRequest}
              style={{ marginTop: "10px" }}
            >
              <i className="bx bx-plus me-1"></i> Add Document Request
            </Button>
          </div>
        </div>

        <ErrorAlert formik={formik} />

        <div className="col-md-12">
          <div className="text-end">
            <Button
              type="submit"
              color="success"
              className="btn-success save-user"
            >
              Save
              <i id="nexticon" className="bx bx-chevron-right ms-1"></i>
              <div
                id="saveSpinner"
                style={{
                  display: "none",
                  height: "15px",
                  width: "15px",
                  marginLeft: "5px",
                }}
                className="spinner-border"
                role="status"
              ></div>
              <i
                id="failedSaveErrorIcon"
                style={{
                  display: "none",
                  marginLeft: "5px",
                }}
                className="fa fa-exclamation-triangle"
                aria-hidden="true"
              ></i>
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
};

export { EditProperty };